<template>
  <div>
    <NewStore @submit="submit" :loading="loading" />
    <CheckoutDialog
      v-if="paymentState.show"
      v-model="paymentState.show"
      :pk="stripePublicKey"
      :client-secret="paymentState.clientSecret!"
      :payment-amount="paymentState.paymentAmount!"
      :plan="paymentState.plan!"
      :workspace-code="paymentState.workspaceCode!"
      :subscription-details="paymentState.subscriptionDetails"
      title="Complete Your Store Setup"
      subtitle="Secure payment to activate your plan"
      context="store-creation"
      intent-type="setup"
    />
  </div>
</template>

<script lang="ts">
import NewStore from "@/components/pages/new-store";
import CheckoutDialog from "@/components/subscription/CheckoutDialog.vue";
import type { SubmitForm } from "~/components/pages/new-store/types";
import type { CreateStoreInput } from "~/composables/store-creation";
import type { PlanSubscriptionDetails } from "~/composables/plan-subscription";
import { notify } from "@kyvg/vue3-notification";
import { omit } from "lodash";

export default defineComponent({
  components: { NewStore, CheckoutDialog },
  setup() {
    definePageMeta({
      layout: "canvas",
    });

    const config = useRuntimeConfig();
    const stripePublicKey = config.public.stripe.pk;

    const pageLoader = useLoading();
    const { createStore } = useStoreCreation();
    const { waitForWorkspaceReady, exited } = useWorkspaceSetup();
    const { loading, handle, onResult, onError } = observeAsync(createStore);

    const paymentState = reactive<{
      show: boolean;
      clientSecret: string | null;
      paymentAmount: number | null;
      plan: string | null;
      workspaceCode: string | null;
      subscriptionDetails: PlanSubscriptionDetails | undefined;
    }>({
      show: false,
      clientSecret: null,
      paymentAmount: null,
      plan: null,
      workspaceCode: null,
      subscriptionDetails: undefined,
    });

    const waitForWorkspaceSetup = (workspace: string) => {
      pageLoader.loading(true, "Preparing workspace");

      waitForWorkspaceReady(workspace, {
        maxWaitTime: 1000 * 60 * 2, // 2 minutes
        navigate: true, // automatically switch to new workspace
      });

      // Show "Still preparing" after 5 seconds
      setTimeout(() => {
        if (!exited.value) {
          pageLoader.loading(true, "Still preparing workspace");
        }
      }, 5000);
    };

    onResult((result) => {
      if (!result.workspace?.code) {
        return;
      }

      if (result.requires_payment) {
        // Show payment dialog
        paymentState.show = true;
        paymentState.clientSecret = result.setup_intent_secret!;
        paymentState.paymentAmount = result.payment_amount!;
        paymentState.workspaceCode = result.workspace.code;
        paymentState.subscriptionDetails = result.subscription_details;

        // Extract plan from the form submission (stored during submit)
        paymentState.plan = currentPlanHandle.value;
      } else {
        // No payment required, wait for workspace setup
        waitForWorkspaceSetup(result.workspace.code);
      }
    });

    onError((error) => {
      console.error(error);
      notify({
        title: "Store",
        type: "error",
        text: `Error creating your store`,
      });
    });

    // Store the current plan handle for payment confirmation
    const currentPlanHandle = ref<string | null>(null);

    const submit = (form: SubmitForm) => {
      const data: CreateStoreInput = omit(form, ["plan_period", "plan"]);

      if (form.plan !== "none" && form.plan) {
        data.plan = form.plan;
        currentPlanHandle.value = form.plan; // Store for later use in payment
      }

      if (form.plan_period) {
        data.plan_period = periodLikeToInterval(form.plan_period);
      }

      return handle(data);
    };

    return {
      loading,
      submit,
      paymentState,
      stripePublicKey,
    };
  },
});
</script>
